import DocPageStyles from './doc-center.module.scss';
import CommonCard from '../../components/docPageComponents/commonCard/common-card';
import DocSearchTool from '../../components/docPageComponents/doc-search-tool';
import MyAnchor from '../../components/docPageComponents/anchor';
import ProductGuideline from '../../components/docPageComponents/product-guideline';
import { ProductInfo, getProductList } from '../../services/document';
import { groupBy } from 'lodash';
import { useUserAgentInfo } from '../../context/user-agent';
import { getKconfContent } from '@/services/common';
import FadeIn from '@/components/commonComponents/fadeIn';
import { useEffect, useMemo, useState } from 'react';
import { getOrderedProductWithType } from '../../components/docPageComponents/utils';
import { WebPNativeImage } from '@/components/WebPImage';
import { src } from '@/utils/common';
import { defaultCmsConfig } from '@/utils/config';

export const getStaticProps = async () => {
  const res = await getProductList();
  // 获取CMS相关配置
  let cmsConfig = await getKconfContent(
    'website_cms_center',
    'platform_web',
    'streamlake'
  );
  /**
   * NOTE: 如果没有以下的if，文档中心页的处事状态是没有内容的，除非在run-time触发重新渲染
   * 这是因为在编译环节，无法拿到配置在容器云环境变量里的KCONF_TOKEN，没有cmsConfig导致页面无法渲染为预期的效果
   * 考虑到这个config并不会频繁变更，这里的兜底方式是取放在工程里的兜底配置
   * 如果遇到需要更新这个页面的场景，可以重新编译或者手动调用/api/document-revalidate
   */
  if (!cmsConfig || Object.keys(cmsConfig).length === 0) {
    cmsConfig = defaultCmsConfig;
  }
  return {
    props: { productList: res, cmsConfig },
  };
};

const bannerBackground = '/img/doc-center/banner-v4.webp';

type DocCenterProps = {
  productList: ProductInfo[];
  cmsConfig: {
    hotSearchKeys: string[];
    productTypeOrder: string[];
  };
};

export default function DocPage({ productList, cmsConfig }: DocCenterProps) {
  const productListByType = useMemo(
    () => groupBy(productList, 'firstType'),
    [productList]
  );
  const productTypes = Object.keys(productListByType);
  const [orderedProductTypes, setOrderedProductTypes] = useState<
    {
      productList: ProductInfo[];
      productType: string;
    }[]
  >(
    (cmsConfig?.productTypeOrder &&
      productListByType &&
      getOrderedProductWithType<ProductInfo>(
        cmsConfig.productTypeOrder,
        productListByType
      )) ||
      []
  );

  useEffect(() => {
    if (!cmsConfig?.productTypeOrder) return;
    const res = getOrderedProductWithType(
      cmsConfig.productTypeOrder,
      productListByType
    );
    setOrderedProductTypes(res);
  }, [productListByType]);

  // 左侧锚点
  const anchorList = orderedProductTypes.map((item) => {
    return {
      href: `#${item.productType}`,
      title: item.productType,
    };
  });
  const userAgentInfo = useUserAgentInfo();

  if (userAgentInfo.isMobile) {
    return (
      <div className={DocPageStyles['container-mobile']}>
        <div className={DocPageStyles['content']}>
          {productTypes.map((item) => (
            <div className={DocPageStyles.card} key={item}>
              <CommonCard
                productType={item}
                mediaDesc={productListByType[item]}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className={DocPageStyles.container}>
      <div className={DocPageStyles.banner}>
        <div className={DocPageStyles.backgroundContainer}>
          <WebPNativeImage
            src={src(bannerBackground)}
            alt="banner"
            className={DocPageStyles.background}
            crossOrigin="anonymous"
          />
        </div>
        <div className={DocPageStyles.box}>
          <FadeIn direction={null}>
            <div className={DocPageStyles.partTitle}>
              <div className={DocPageStyles.title}>文档中心</div>
              <DocSearchTool hotSearchKeys={cmsConfig.hotSearchKeys} />
            </div>
          </FadeIn>
        </div>
      </div>
      <div className={DocPageStyles.box}>
        <div className={DocPageStyles.partContent}>
          <FadeIn direction={null}>
            <div className={DocPageStyles.contentBox}>
              <section className={DocPageStyles.contentSide}>
                <MyAnchor data={anchorList} />
              </section>
              <section className={DocPageStyles.content}>
                <ProductGuideline data={orderedProductTypes} />
              </section>
            </div>
          </FadeIn>
        </div>
      </div>
    </div>
  );
}
